import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar';
import { useState, useEffect, useRef } from "react";
import {
    BrowserRouter as Router,
    useHistory,
    useLocation
} from "react-router-dom";
import { renderRoutes } from "react-router-config";
import CssBaseline from '@material-ui/core/CssBaseline';
// We need to import the server stylesheet explicity so the module is detected
import styles from './styles/main.css'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { ThemeProvider } from '@material-ui/core/styles';
import imunisTheme from './styles/theme'
import { GlobalProvider } from "./components/context/globalState";
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { App as CapacitorApp } from '@capacitor/app';
import { scrubbedURL } from './utils/common';
import mixpanel from 'mixpanel-browser';
import { routes } from './routes';

// Intro
import { Splash } from "./pages/splash";

import { initialiseInterceptors } from './utils/axiosApi';
import ActionDialog from './components/dialogs/action_dialog';
import { BranchBadge } from './components/branch_badge';

if (Capacitor.getPlatform() === 'android') {
    // Ensuring that on android the statusbar doesn't overlap the webview
    // We won't dynamically change the android statusbar since it's not overlaid
    void StatusBar.setOverlaysWebView({ overlay: false })
}

/** Handles iOS native app routing */
const AppUrlListener = () => {
    const history = useHistory()

    useEffect(() => {
        CapacitorApp.addListener('appUrlOpen', (event) => {
            const url = new window.URL(event.url)
            history.push(url.pathname)
        })

        return () => {
            CapacitorApp.removeAllListeners()
        }
    }, [])

    return null
}

/**
 * Provide the Axios interceptors with the history instance so we can do in-app navigation.
 */
const AxiosInterceptors = () => {
    const history = useHistory()

    useEffect(() => {
        initialiseInterceptors(history)
    }, [history])

    return null
}

/**
 * A dummy component (returns `null`) that just installs an app-wide hook in order to send page view
 * events to analytics (GA, Mixpanel).
 *
 * We do this manually in order to handle masking sensitive data (invite tokens) and to de-duplicate
 * URLs that contain user UUIDs.
 */
const PageTracker = () => {
    const location = useLocation()

    useEffect(() => {

        /**
         * Update page location -- any other events sent to GA on the same page will include the
         * `page_location` value set here. Note GA uses the full URL of the page for `page_location`.
         *
         * Ensure GA is configured to not track page views automatically:
         * https://support.google.com/analytics/answer/9216061
         *
         */
        window.gtag("set", {
            page_location: document.location.origin + scrubbedURL(`${location.pathname}${location.search}`),
        });

        // https://developers.google.com/tag-platform/gtagjs/reference/events#page_view
        window.gtag('event', 'page_view', {
            page_title: document.title,
            user_agent: navigator.userAgent,
        })

        /**
         * Send page views to Mixpanel (auto tracking is disabled by default).
         * Override Mixpanel's `current_url(path|search)` keys with their sanitised versions.
         *
         * See:
         * https://docs.mixpanel.com/docs/tracking/reference/javascript#tracking-page-views
         * https://github.com/mixpanel/mixpanel-js/blob/master/src/utils.js#L1649
         */
        mixpanel.track_pageview({
            current_url_path: scrubbedURL(location.pathname),
            current_url_search: scrubbedURL(location.search),
        })

    }, [location.pathname, location.search])

    return null
}

/**
 * Frontend application routes are defined in `./routes.js`.
 * By convention paths should be param-cased and not end with a trailing slash.
 */
const AppRoutes = () => renderRoutes(routes)

const App = ({splash}) => {
    const isHomePage = window.location.pathname === '/'
    const [showSplash, setSplash] = useState(splash)
    const [exitPrompt, setExitPrompt] = useState(false)
    const exitPromptCallbackRef = useRef()

    useEffect(() => {
        setTimeout(() => {
            setSplash(false)
        }, imunisTheme.transitions.duration.short)
    });

    useEffect(() => {
        // Set native status bar color for iOS
        if (Capacitor.getPlatform() === 'ios') {
            void StatusBar.setStyle({ style: Style.Light });
        }

        const jssStyles = document.getElementById('jss-server-side');
        if (jssStyles) {
            jssStyles.parentElement.removeChild(jssStyles);
        }
    }, []);

    if (showSplash) {
        return <Splash visible={showSplash}/>
    }

    return <>
        <Splash visible={showSplash} noBackground={isHomePage}/>
        <ThemeProvider theme={imunisTheme}>
            <GlobalProvider>
                <CssBaseline/>
                <BranchBadge/>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <Router getUserConfirmation={(message, callback) => {
                        setExitPrompt(message)
                        exitPromptCallbackRef.current = callback
                    }}>
                        <ActionDialog
                            title="Leave page?"
                            content={exitPrompt}
                            openDialog={Boolean(exitPrompt)}
                            setOpenDialog={setExitPrompt}
                            action_label="OK"
                            action_callback={() => exitPromptCallbackRef.current(true)}
                            cancel_callback={() => exitPromptCallbackRef.current(false)}
                            close_callback={() => exitPromptCallbackRef.current(false)}
                            cancel={true} />

                        <AppUrlListener/>
                        <AxiosInterceptors/>
                        <PageTracker/>
                        <AppRoutes/>
                    </Router>
                </MuiPickersUtilsProvider>
            </GlobalProvider>
        </ThemeProvider>
    </>;
}

export default App;
